import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import EfficientFrontierSection from '../components/EfficientFrontierChart';
import SegmentationModal from '../components/SegmentationModal';
import NeuralNetwork from '../components/NeuralNetwork';
import FAQSection from '../components/FAQSection';

/* ── Simple line icons ── */
function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="4" />
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}

function ScaleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18" />
      <path d="M5 8l7-5 7 5" />
      <path d="M3 14l2-6 4 6a4 4 0 0 1-6 0z" />
      <path d="M15 14l2-6 4 6a4 4 0 0 1-6 0z" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function SavingsProduct() {
  const [showSegModal, setShowSegModal] = useState(false);

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Savings Pricing Optimisation | Clariti AI</title>
        <meta name="description" content="Savings pricing optimisation software for banks. Improve deposit pricing, manage funding costs, and increase retention with data-driven savings analytics." />
        <link rel="canonical" href="https://www.clariti.ai/products/savings" />
        <meta property="og:title" content="Savings Pricing Optimisation Software | Clariti AI" />
        <meta property="og:description" content="Optimise savings rates and deposit strategy with data-driven models for treasury and pricing teams." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.clariti.ai/products/savings" />
        <meta name="twitter:title" content="Savings Pricing Optimisation Software | Clariti AI" />
        <meta name="twitter:description" content="Data-driven savings pricing software for better retention and funding outcomes." />
      </Helmet>
      {/* 1. Hero */}
      <section className="pp-hero">
        <NeuralNetwork className="nn-layer" nodeCount={40} interactive={true} />
        <div className="container">
          <div className="pp-hero-inner">
            <h1>Savings pricing optimisation</h1>
            <p>
              Our savings pricing optimisation software helps banks manage deposit pricing,
              retention, and funding cost trade-offs with data-driven rate strategy.
            </p>
            <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
          </div>
        </div>
      </section>

      {/* 2. The problem */}
      <section className="pp-problem">
        <NeuralNetwork className="nn-layer" nodeCount={20} interactive={false} />
        <div className="container">
          <div className="pp-problem-inner">
            <p>
              Pricing is complex — balancing margin against volume, acquisition against
              retention, competitiveness against profitability. Most banks simplify it by
              guessing or copying competitors. We help you turn that complexity into an edge.
            </p>
          </div>
        </div>
      </section>

      {/* 3. What you get */}
      <section className="pp-cards-section">
        <NeuralNetwork className="nn-layer" nodeCount={18} interactive={false} />
        <div className="container">
          <div className="section-header">
            <h2>What you get</h2>
          </div>
          <div className="pp-cards-grid">
            <div className="pp-card">
              <div className="pp-card-icon"><ChartIcon /></div>
              <h3>Real-time competitive intelligence</h3>
              <p>
                Know exactly where you sit against competitors — and which competitors
                actually matter to your customers. Updated daily.
              </p>
            </div>
            <div className="pp-card">
              <div className="pp-card-icon"><PeopleIcon /></div>
              <h3>Customer-level behavioural modelling</h3>
              <p>
                Segmented models that predict how price-sensitive vs. non-price-sensitive
                customers respond to rate changes — so you're not optimising for averages.
              </p>
            </div>
            <div className="pp-card">
              <div className="pp-card-icon"><ScaleIcon /></div>
              <h3>Complete trade-off visibility</h3>
              <p>
                See exactly what each basis point costs you in volume and what it's worth
                in margin. Model cannibalisation effects across your entire product portfolio.
              </p>
            </div>
            <div className="pp-card">
              <div className="pp-card-icon"><TargetIcon /></div>
              <h3>Portfolio optimisation</h3>
              <p>
                Find the optimal rate configuration across your full portfolio — respecting
                your constraints (board limits, compliance, pricing principles) while
                maximising margin for any balance target.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. How it works */}
      <section className="pp-timeline-section">
        <NeuralNetwork className="nn-layer" nodeCount={20} interactive={false} />
        <div className="container">
          <div className="section-header">
            <h2>How it works</h2>
          </div>
          <div className="pp-timeline">
            <div className="pp-timeline-step">
              <div className="pp-timeline-badge">1</div>
              <div className="pp-timeline-text">
                <h3>Competitor intelligence</h3>
                <p>
                  We map your competitive landscape — identifying which competitors actually
                  influence your customers' decisions and tracking their rate positions in real-time.
                </p>
              </div>
            </div>
            <div className="pp-timeline-step">
              <div className="pp-timeline-badge">2</div>
              <div className="pp-timeline-text">
                <h3>Customer segmentation</h3>
                <p>
                  We segment your book into price-sensitive and non-price-sensitive customers,
                  so we can model behavioural responses accurately for each group.
                </p>
                <button
                  onClick={() => setShowSegModal(true)}
                  className="btn btn-secondary"
                  style={{ marginTop: '12px', fontSize: '0.85rem', padding: '8px 18px' }}
                >
                  See segmentation in action →
                </button>
              </div>
            </div>
            <div className="pp-timeline-step">
              <div className="pp-timeline-badge">3</div>
              <div className="pp-timeline-text">
                <h3>What-if modelling</h3>
                <p>
                  We build regression models that predict exactly what happens to volume, margin,
                  and cannibalisation across products when you change rates — giving you complete
                  visibility into every trade-off.
                </p>
              </div>
            </div>
            <div className="pp-timeline-step">
              <div className="pp-timeline-badge">4</div>
              <div className="pp-timeline-text">
                <h3>Portfolio optimisation</h3>
                <p>
                  We feed everything into an optimisation engine that respects your constraints
                  (board limits, compliance requirements, pricing principles) and finds the optimal
                  rate configuration across your entire portfolio.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Efficient frontier (sticky scroll interactive) */}
      <EfficientFrontierSection />

      {/* 6. FAQ */}
      <FAQSection title="Savings pricing FAQs" faqs={[
        {
          question: 'How does Clariti AI help with savings pricing?',
          answer: 'We model how your customers respond to rate changes at a granular level — distinguishing rate-sensitive savers from sticky depositors. This lets you set rates that retain the balances you want while managing your overall cost of funding.'
        },
        {
          question: 'What data do you need for savings optimisation?',
          answer: 'We typically need product-level portfolio data including balances, rates, and account-level flow history where available. We combine this with our own competitive intelligence on the savings market. Most banks can provide what we need without IT integration upfront.'
        },
        {
          question: 'Can you model the impact of rate changes on deposit retention?',
          answer: 'Yes — retention modelling is central to our savings product. We quantify how rate changes affect attrition and new money flows across different customer segments, so you can see the full funding impact before making a change.'
        },
        {
          question: 'How does competitive benchmarking work for savings?',
          answer: 'We track competitor savings rates across the market daily, broken down by product type, term, and access conditions. Our models then link your competitive position to actual customer behaviour, so you know which competitor moves actually affect your flows.'
        },
        {
          question: 'Does Clariti AI work for both fixed-term and easy access savings?',
          answer: 'Yes. Our platform handles the full range of savings products — easy access, notice accounts, fixed-term bonds, and ISAs. Each product type has distinct customer behaviour patterns, and our models are calibrated accordingly.'
        },
      ]} />

      {/* 7. CTA */}
      <section className="cta-section">
        <NeuralNetwork className="nn-layer" nodeCount={30} interactive={true} />
        <div className="container">
          <div className="cta-content">
            <h2>See it in action</h2>
            <p>Book a 30-minute demo to see how we model your savings portfolio.</p>
            <div className="cta-button-group">
              <Link to="/contact" className="btn btn-white">Book a Demo</Link>
              <Link to="/insights" className="btn btn-white">Read Pricing Insights</Link>
            </div>
          </div>
        </div>
      </section>

      <SegmentationModal isOpen={showSegModal} onClose={() => setShowSegModal(false)} />
    </div>
  );
}

export default SavingsProduct;






